import { type AxisAlignedBox, HierarchyUtils, MeasurementUtils, type Nullable } from '@inter-ikea-core/core'

import type { SofaPlatformCore } from '../core-api'
import { getNextBryggaStateFromEntity, toggleBryggaState } from './brygga-state'

/**
 * Gets the outside measurements (aabb) of all entities in the scene.
 *
 * @returns A single AABB if `includeVariations` is `false` or `undefined`
 * or a tuple of AABB if `includeVariations` is `true`.
 *
 * @example
 * const aabb = getOutsideMeasurements(core);
 * const [aabb, variationAabb] = getOutsideMeasurements(core, true);
 */
export function getOutsideMeasurements(
  core: SofaPlatformCore,
  includeVariations: true,
): [Nullable<AxisAlignedBox>, Nullable<AxisAlignedBox>]
export function getOutsideMeasurements(core: SofaPlatformCore): Nullable<AxisAlignedBox>
export function getOutsideMeasurements(core: SofaPlatformCore, includeVariations?: boolean) {
  const entities = HierarchyUtils.descendants(core.coreServices.sceneManager.scene)
  const aabb = MeasurementUtils.getOutsideMeasurement(entities)
  if (!includeVariations) return aabb
  const variationAabb = getBryggaStateVariationMeasurements(core)
  return [aabb, variationAabb]
}

function getBryggaStateVariationMeasurements(core: SofaPlatformCore) {
  const scene = core.coreServices.sceneManager.scene
  const hasStates = HierarchyUtils.descendants(scene).some((entity) => getNextBryggaStateFromEntity(entity, 'sofabed'))
  if (!hasStates) return null

  const sceneCopy = HierarchyUtils.duplicateEntity(scene, core.coreServices)
  void toggleBryggaState(core, 'sofabed', sceneCopy)
  const entities = HierarchyUtils.descendants(sceneCopy)
  const aabb = MeasurementUtils.getOutsideMeasurement(entities)
  sceneCopy.dispose()
  return aabb
}
